import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import RichTextEditor from "components/Shared/RichTextEditor";
import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import {
  useGetDDLDriversForDeliveryQuery,
  useLazyGetDriverShipmentsWithPricesQuery,
} from "store/services/driverApi";
import {
  useLazyGetDDLSystemUserListQuery,
  useLazyGetDDLSystemUserListShipmentQuery,
} from "store/services/userApi";
import { useListGlobalListQuery } from "store/services/globalListApi";
import { useListListQuery } from "store/services/listService";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { getSocketInstance } from "socket/socket.io";
import NCHLQR from "utils/generateNCHLQRCode";

const DeliveryCollectionForm = ({
  isBusy = null,
  deliveryCollectionDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();

  const [pricingData, setPricingData] = useState([]);
  const [selectedPriceIds, setSelectedPriceIds] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [invalidShipments, setInvalidShipments] = useState([]);
  const nchlQRRef = useRef(null);
  const paymentSuccessHandled = useRef(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [paymentResponseData, setPaymentResponseData] = useState(null);
  const [currentTraceId, setCurrentTraceId] = useState(null);
  const [driverShipments, setDriverShipments] = useState([]);
  const previousDriverRef = useRef(null);

  const defaultValues = useMemo(() => ({
    account: viewEntity?.entity?.account || "",
    entity: viewEntity?.entity || "",
    deliveryType: "",
    driver: "",
    responsible: "",
    remittanceRemarks: "",
    shipment: null,
    priceIds: [],
    collectionMode: "",
    bankName: "",
    actualAmount: 0,
    collectedAmount: 0,
    notes: "<p>Enter Notes.</p>",
  }), [viewEntity]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({ defaultValues });

  const watchedValues = useWatch({ control });
  const {
    entity: _watchEntity,
    account: _watchAccount,
    deliveryType: _watchDeliveryType,
    driver: _watchDriver,
    responsible: _watchResponsible,
    shipment: _watchShipment,
    priceIds: _watchPriceIds,
    actualAmount: _watchActualAmount,
    collectedAmount: _watchCollectedAmount,
    collectionMode: _watchTransferMode,
  } = watchedValues;

  // API Queries
  const { data: entityTypeList = [] } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.PAYMENT_METHOD,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const { data: bankNameOptions, isLoading: loadingBankNameOptions } =
    useListGlobalListQuery({
      type: GLOBAL_LIST_CONSTANTS.BANK_NAME,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const {
    data: drivers = [],
    isLoading: loadingDrivers,
    error: driversError,
  } = useGetDDLDriversForDeliveryQuery(
    {
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
      active: "true",
    },
    {
      skip: !_watchAccount?._id || !_watchEntity?._id || _watchDeliveryType !== "Home Delivery",
    }
  );

  const [
    fetchDriverShipments,
    {
      isLoading: loadingDriverShipments,
      error: driverShipmentsError,
    },
  ] = useLazyGetDriverShipmentsWithPricesQuery();

  const [
    fetchUsers,
    { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const [
    fetchUserWithShipments,
    {
      data: userWithShipments,
      isLoading: loadingUserShipments,
      error: userShipmentsError,
    },
  ] = useLazyGetDDLSystemUserListShipmentQuery();

  // Memoized computed values
  const _mappedAccountTypeList = useMemo(
    () =>
      Array.isArray(entityTypeList)
        ? entityTypeList.map((item) => ({
          label: item?.name || "",
          value: item?._id || "",
        }))
        : [],
    [entityTypeList]
  );

  const selectedCollectionMethod = useMemo(() => {
    if (!Array.isArray(entityTypeList) || !_watchTransferMode) return null;
    return entityTypeList.find((item) => item._id === _watchTransferMode);
  }, [_watchTransferMode, entityTypeList]);
  const isNCHL = selectedCollectionMethod?.name === "NCHL";
  const formattedBankNameOptions = useMemo(
    () =>
      bankNameOptions?.map((option) => ({
        value: option._id,
        label: option.name,
      })) || [],
    [bankNameOptions]
  );

  const _mappedUsers = useMemo(
    () =>
      userList?.map((el) => ({
        label: el?.name,
        id: el?._id,
      })) || [],
    [userList]
  );


  const responsibleUserShipments = useMemo(() => {
    if (
      _watchDeliveryType !== "Branch Delivery" ||
      !userWithShipments ||
      !Array.isArray(userWithShipments)
    ) {
      return [];
    }
    const userData = userWithShipments[0];
    return userData?.shipments || [];
  }, [userWithShipments, _watchDeliveryType]);

  // Effects
  const clearFormSelections = useCallback(() => {
    setValue("driver", "");
    setValue("responsible", "");
    setValue("shipment", []);
    setValue("priceIds", []);
    setSelectedPriceIds([]);
    setPricingData([]);
    setDriverShipments([]);
    previousDriverRef.current = null;
  }, [setValue]);

  // Fetch entities when account changes
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({
        account: _watchAccount._id,
      });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch users and shipments when entity changes
  useEffect(() => {
    if (!_watchAccount?._id || !_watchEntity?._id) return;
    fetchUsers({ entity: _watchEntity._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchUsers]);

  // Fetch user shipments when responsible user changes
  useEffect(() => {
    if (
      _watchDeliveryType === "Branch Delivery" &&
      _watchResponsible?.id &&
      _watchAccount?._id &&
      _watchEntity?._id
    ) {
      fetchUserWithShipments({
        userId: _watchResponsible.id,
        account: _watchAccount._id,
        entity: _watchEntity._id,
        active: "true",
      });
    }
  }, [
    _watchResponsible?.id,
    _watchAccount?._id,
    _watchEntity?._id,
    _watchDeliveryType,
    fetchUserWithShipments,
  ]);

  // FIXED: Handle driver shipments for Home Delivery without infinite loop
  useEffect(() => {
    // Skip if not home delivery or no driver selected
    if (_watchDeliveryType !== "Home Delivery" || !_watchDriver?.value) {
      setDriverShipments([]);
      setValue("shipment", []);
      return;
    }

    // Skip if driver hasn't changed
    if (previousDriverRef.current === _watchDriver.value) {
      return;
    }

    // Update previous driver reference
    previousDriverRef.current = _watchDriver.value;

    // Clear previous shipments immediately
    setDriverShipments([]);
    setValue("shipment", []);

    // Fetch new driver shipments
    fetchDriverShipments({
      driverId: _watchDriver.value,
      account: _watchAccount._id,
      entity: _watchEntity._id,
    })
      .unwrap()
      .then((shipmentsData) => {
        const validShipments = (shipmentsData || []).filter(s => s._id);
        setDriverShipments(validShipments);
      })
      .catch((error) => {
        console.error("Failed to fetch driver shipments:", error);
        setDriverShipments([]);
        setValue("shipment", []);
      });
  }, [
    _watchDriver?.value,
    _watchDeliveryType,
    _watchAccount?._id,
    _watchEntity?._id,
    fetchDriverShipments,
    setValue,
  ]);

  // Auto-select shipments based on delivery type
  useEffect(() => {
    if (_watchDeliveryType === "Home Delivery" && driverShipments.length > 0) {
      if (isNCHL) {
        if (driverShipments.length === 1) {
          setValue("shipment", driverShipments[0]);
          setValue("remittanceRemarks", driverShipments[0]?.shipmentId || "");
        } else {
          setValue("shipment", null);
        }
      } else {
        setValue("shipment", driverShipments);
      }
    } else if (_watchDeliveryType === "Branch Delivery" && responsibleUserShipments.length > 0) {
      if (isNCHL) {
        if (responsibleUserShipments.length === 1) {
          setValue("shipment", responsibleUserShipments[0]);
          setValue("remittanceRemarks", responsibleUserShipments[0]?.shipmentId || "");
        } else {
          setValue("shipment", null);
        }
      } else {
        setValue("shipment", responsibleUserShipments);
      }
    } else if (_watchDeliveryType) {
      setValue("shipment", isNCHL ? null : []);
    }
  }, [_watchDeliveryType, driverShipments, responsibleUserShipments, setValue, isNCHL]);

  // Process pricing data for selected shipments
  useEffect(() => {
    if (userShipmentsError || driverShipmentsError) {
      toast.error("Failed to load shipments. Please try again.");
      return;
    }

    let filteredPriceItems = [];

    if (_watchDeliveryType === "Branch Delivery" && Array.isArray(responsibleUserShipments)) {
      const selectedShipmentIds = isNCHL
        ? (_watchShipment?._id ? [_watchShipment._id] : [])
        : (Array.isArray(_watchShipment) ? _watchShipment.map((s) => s._id) : []);
      filteredPriceItems = responsibleUserShipments
        .filter((shipment) => selectedShipmentIds.includes(shipment._id))
        .flatMap((shipment) =>
          (shipment.priceItems || []).map((price) => ({
            shipmentId: shipment.shipmentId,
            price: {
              _id: price._id,
              feeItem: price.feeItem || "Unknown",
              charge: price.charge || 0,
              payer: price.payer || "Unknown",
              paymentLocation: price.paymentLocation || "Unknown",
              user: price.responsibleUser || "Unknown",
              createdAt: price.createdAt || new Date(),
              shipmentId: price.shipmentId || "",
              paymentStatus: price.paymentStatus || "Not Paid",
            },
          }))
        );
    } else if (_watchDeliveryType === "Home Delivery") {
      const selectedShipmentIds = isNCHL
        ? (_watchShipment?._id ? [_watchShipment._id] : [])
        : (Array.isArray(_watchShipment) ? _watchShipment.map((s) => s._id) : []);
      filteredPriceItems = driverShipments
        .filter((shipment) => selectedShipmentIds.includes(shipment._id))
        .flatMap((shipment) =>
          (shipment.priceItems || []).map((price) => ({
            shipmentId: shipment.shipmentId,
            price: {
              _id: price._id,
              feeItem: price.feeItem || "Unknown",
              charge: price.charge || 0,
              payer: price.payer || "Unknown",
              paymentLocation: price.paymentLocation || "Unknown",
              user: price.responsibleUser || "Unknown",
              createdAt: price.createdAt || new Date(),
              shipmentId: price.shipmentId || "",
              paymentStatus: price.paymentStatus || "Not Paid",
            },
          }))
        );
    }

    setPricingData(filteredPriceItems);

    // Initialize price IDs for edit mode
    if (deliveryCollectionDetail && filteredPriceItems.length > 0) {
      const cocPriceIds = (deliveryCollectionDetail.priceIds || []).map(
        (p) => p._id || p
      );
      const matchingPriceIds = filteredPriceItems
        .filter((item) => cocPriceIds.includes(item.price._id))
        .map((item) => item.price);
      setValue("priceIds", matchingPriceIds);
      setSelectedPriceIds(matchingPriceIds.map((p) => p._id));
    } else if (!deliveryCollectionDetail) {
      const allIds = filteredPriceItems.map((item) => item.price._id);
      setSelectedPriceIds(allIds);
      setValue(
        "priceIds",
        filteredPriceItems.map((item) => item.price)
      );
    }
  }, [
    _watchShipment,
    _watchDeliveryType,
    responsibleUserShipments,
    driverShipments,
    deliveryCollectionDetail,
    setValue,
    userShipmentsError,
    driverShipmentsError,
    isNCHL,
  ]);

  // Update actual and collected amounts
  useEffect(() => {
    const totalAmount = _watchPriceIds?.reduce((sum, price) => sum + (price.charge || 0), 0) || 0;
    setValue("actualAmount", totalAmount);
    setValue("collectedAmount", totalAmount);
  }, [_watchPriceIds, setValue]);

  const unpaidPriceIds = useMemo(() => {
    if (!_watchPriceIds?.length) return [];
    return _watchPriceIds
      .filter((price) => price.paymentStatus !== "Paid")
      .map((price) => price._id)
      .filter(Boolean);
  }, [_watchPriceIds]);

  const handleNCHLPaymentSuccess = useCallback(
    (response) => {
      // Reentrancy guard. `handleNCHLPaymentSuccess` can fire from two
      // independent sources for the same payment: (1) NCHLQR's onSuccess
      // prop when debitStatus === "000", and (2) the parent's socket
      // listener on `payment-success/delivery-collection/${currentTraceId}`.
      // Both can arrive close together — without this guard we toast and
      // navigate twice. Reset in the priceIds-watcher effect below so a
      // new payment attempt can notify again.
      if (paymentSuccessHandled.current) return;
      paymentSuccessHandled.current = true;
      let paymentData = null;
      let deliveryCollectionId = null;
      let redirectUrl = null;

      if (response?.deliveryCollectionId) {
        deliveryCollectionId = response.deliveryCollectionId;
        redirectUrl = response.redirectUrl;
        paymentData = response.paymentData;
      } else if (response?.paymentData) {
        paymentData = response.paymentData;
        deliveryCollectionId = response.paymentData.deliveryCollectionRef;
      } else if (response?.sessionSrlNo || response?.debitStatus) {
        // Raw NCHL status only — the debit is confirmed but the backend
        // hasn't finished creating the collection record yet (that only
        // happens via the websocket messageHandler). Don't treat this as
        // terminal: there's no redirect data yet, and locking the guard
        // here would silently swallow the real socket event when it
        // arrives. Reset the guard and bail so the follow-up call from
        // the socket can still do the work.
        paymentSuccessHandled.current = false;
        toast.info("Payment confirmed, finalizing collection...");
        return;
      }

      if (paymentData) {
        setPaymentResponseData(paymentData);
        toast.success("NCHL Payment successful! Delivery Collection created.");
        nchlQRRef.current?.handleCloseQR();
        setIsPaymentDone(true);

        if (deliveryCollectionId && redirectUrl) {
          toast.success("Redirecting to Delivery Collection details...", { autoClose: 1500 });
          setTimeout(() => navigate(redirectUrl), 1500);
        } else if (deliveryCollectionId) {
          const fallbackUrl = `/app/crm/delivery-collection/${deliveryCollectionId}/detail`;
          toast.success("Redirecting to Delivery Collection details...", { autoClose: 1500 });
          setTimeout(() => navigate(fallbackUrl), 1500);
        } else {
          console.error("No Delivery Collection ID found for redirect");
        }
      } else {
        // No usable payment data — let a future attempt re-fire the
        // success handler instead of permanently swallowing it.
        paymentSuccessHandled.current = false;
      }
    },
    [navigate]
  );

  useEffect(() => {
    setIsPaymentDone(false);
    setPaymentResponseData(null);
    // Allow a fresh NCHL payment attempt to re-fire onSuccess now that
    // the selected priceIds have changed (a new payment flow).
    paymentSuccessHandled.current = false;
  }, [_watchPriceIds]);

  useEffect(() => {
    if (!currentTraceId) return;
    const socket = getSocketInstance();
    if (!socket) return;
    const uniqueEvent = `payment-success/delivery-collection/${currentTraceId}`;
    const handleSuccess = (response) => {
      if (response.validationTraceId !== currentTraceId) return;
      handleNCHLPaymentSuccess(response);
    };
    socket.on(uniqueEvent, handleSuccess);
    return () => {
      // ONLY remove the listener here. Do NOT call setCurrentTraceId(null)
      // — React.StrictMode deliberately unmounts/remounts effects twice in
      // dev, and resetting currentTraceId in cleanup causes the second
      // mount's effect to see null and exit early, leaving the listener
      // permanently unregistered. The next setCurrentTraceId(newTraceId)
      // call from the click handler will trigger a re-run that swaps in
      // the new listener correctly.
      socket.off(uniqueEvent, handleSuccess);
    };
  }, [currentTraceId, handleNCHLPaymentSuccess]);

  // Clear bank name when collection method is not Bank
  useEffect(() => {
    if (selectedCollectionMethod?.name !== "Bank") {
      setValue("bankName", "");
    }
  }, [selectedCollectionMethod, setValue]);

  // Initialize form with deliveryCollectionDetail
  useEffect(() => {
    if (!deliveryCollectionDetail || loadingBankNameOptions) return;

    const bankNameValue = deliveryCollectionDetail.bankName?._id
      ? formattedBankNameOptions.find(
        (option) => option.value === deliveryCollectionDetail.bankName._id
      )?.value || ""
      : "";
    const detailMethod = entityTypeList.find(
      (item) => item._id === deliveryCollectionDetail.collectionMode
    );
    const detailIsNCHL = detailMethod?.name === "NCHL";
    const shipmentArray = deliveryCollectionDetail.shipment?.filter((ship) => ship._id) || [];
    reset({
      account: deliveryCollectionDetail.account || "",
      entity: deliveryCollectionDetail.entity || "",
      deliveryType: deliveryCollectionDetail.driver
        ? "Home Delivery"
        : deliveryCollectionDetail.responsibleUser
          ? "Branch Delivery"
          : "",
      driver: deliveryCollectionDetail.driver
        ? {
          value: deliveryCollectionDetail.driver._id || deliveryCollectionDetail.driver,
          label: deliveryCollectionDetail.driver.name
            ? `${deliveryCollectionDetail.driver.name} (${deliveryCollectionDetail.driver.driverId})`
            : "",
        }
        : "",
      responsible: deliveryCollectionDetail.responsibleUser
        ? {
          label: deliveryCollectionDetail.responsibleUser.name,
          id: deliveryCollectionDetail.responsibleUser._id,
        }
        : "",
      shipment: detailIsNCHL ? (shipmentArray[0] || null) : shipmentArray,
      priceIds: deliveryCollectionDetail.priceIds?.map((p) => ({
        _id: p._id || p,
        feeItem: p.feeItem || "Unknown",
        charge: p.charge || 0,
        payer: p.payer || "Unknown",
        paymentLocation: p.paymentLocation || "Unknown",
        user: p.responsibleUser || "Unknown",
        createdAt: p.createdAt || new Date(),
        shipmentId: p.shipmentId || "",
      })) || [],
      collectionMode: deliveryCollectionDetail.collectionMode || "",
      remittanceRemarks: deliveryCollectionDetail.remittanceRemarks || "",
      bankName: bankNameValue,
      actualAmount: deliveryCollectionDetail.actualAmount || 0,
      collectedAmount: deliveryCollectionDetail.collectedAmount || deliveryCollectionDetail.actualAmount || 0,
      notes: deliveryCollectionDetail.notes || "<p>Enter Notes.</p>",
    });

    // Set initial driver shipments if editing with driver
    if (deliveryCollectionDetail.driver) {
      previousDriverRef.current = deliveryCollectionDetail.driver._id || deliveryCollectionDetail.driver;
    }
  }, [deliveryCollectionDetail, loadingBankNameOptions, formattedBankNameOptions, entityTypeList, reset]);

  // Event handlers
  const handlePriceSelection = useCallback((price) => {
    setSelectedPriceIds(prev => {
      const isSelected = prev.includes(price._id);
      const updatedPriceIds = isSelected
        ? prev.filter((id) => id !== price._id)
        : [...prev, price._id];

      setValue(
        "priceIds",
        pricingData
          .map((shipment) => shipment.price)
          .filter((p) => updatedPriceIds.includes(p._id))
      );

      return updatedPriceIds;
    });
  }, [pricingData, setValue]);

  const handleSelectAll = useCallback(() => {
    const allSelected = selectedPriceIds.length === pricingData.length;

    if (allSelected) {
      setSelectedPriceIds([]);
      setValue("priceIds", []);
    } else {
      const allIds = pricingData.map((item) => item.price._id);
      setSelectedPriceIds(allIds);
      setValue("priceIds", pricingData.map((item) => item.price));
    }
  }, [selectedPriceIds.length, pricingData, setValue]);

  const getCurrentShipmentOptions = useCallback(() => {
    if (_watchDeliveryType === "Home Delivery") {
      return driverShipments;
    } else if (_watchDeliveryType === "Branch Delivery") {
      return responsibleUserShipments;
    }
    return [];
  }, [_watchDeliveryType, driverShipments, responsibleUserShipments]);

  const handleDriverChange = useCallback((data) => {
    // Clear previous data immediately
    setValue("shipment", []);
    setValue("priceIds", []);
    setSelectedPriceIds([]);
    setDriverShipments([]);

    // Update driver field
    setValue("driver", data);
  }, [setValue]);

  const handleResponsibleChange = useCallback((data) => {
    setValue("responsible", data);
    setValue("shipment", []);
    setValue("priceIds", []);
    setSelectedPriceIds([]);
  }, [setValue]);

  const handleShipmentChange = useCallback((data) => {
    setValue("shipment", data);
    setValue("priceIds", []);
    setSelectedPriceIds([]);
    if (isNCHL) {
      setValue("remittanceRemarks", data?.shipmentId || "");
    }
  }, [setValue, isNCHL]);

  const handleDeliveryTypeChange = useCallback((value) => {
    setValue("deliveryType", value);
    setValue("driver", "");
    setValue("responsible", "");
    setValue("shipment", []);
    setValue("priceIds", []);
    setSelectedPriceIds([]);
    setDriverShipments([]);
    previousDriverRef.current = null;
  }, [setValue]);

  const handleAccountChange = useCallback((data) => {
    setValue("account", data);
    setValue("entity", "");
    handleDeliveryTypeChange("");
  }, [setValue, handleDeliveryTypeChange]);

  const handleEntityChange = useCallback((data) => {
    setValue("entity", data);
    handleDeliveryTypeChange("");
  }, [setValue, handleDeliveryTypeChange]);

  const handleDialogConfirm = useCallback((data) => {
    setOpenDialog(false);
    const validShipmentIds = pricingData
      .filter((shipment) => selectedPriceIds.includes(shipment.price._id))
      .map((shipment) => shipment.price.shipmentId);
    const shipmentIds = isNCHL
      ? (data.shipment?._id && validShipmentIds.includes(data.shipment.shipmentId)
        ? [data.shipment._id]
        : [])
      : (Array.isArray(data.shipment)
        ? data.shipment
          .filter((s) => s._id && validShipmentIds.includes(s.shipmentId))
          .map((s) => s._id)
        : []);
    const payload = {
      ...data,
      entity: data.entity?._id || data.entity,
      account: data.account?._id || data.account,
      bankName: data.bankName || "",
      driver: data.deliveryType === "Home Delivery" ? data.driver?.value : undefined,
      responsibleUser: data.deliveryType === "Branch Delivery" ? data.responsible?.id : undefined,
      collectedAmount: data.collectedAmount,
      shipment: shipmentIds,
      priceIds: data.priceIds?.map((p) => p._id) || [],
    };

    onSubmit(payload);
  }, [pricingData, selectedPriceIds, onSubmit, isNCHL]);

  const handleDialogCancel = useCallback(() => {
    setOpenDialog(false);
    toast.error("Shipment's fee item not selected.");
  }, []);

  const handleOnSubmit = useCallback((data) => {
    const actualAmount = Number(data.actualAmount);
    const collectionAmount = Number(data.collectedAmount);

    if (actualAmount !== collectionAmount) {
      toast.error(
        actualAmount > collectionAmount
          ? "Collection Amount is less than Actual Amount."
          : "Collection Amount is more than Actual Amount."
      );
      return;
    }

    if (!_watchPriceIds?.length) {
      toast.error("Please select at least one fee item.");
      return;
    }

    const priceShipmentIds = pricingData
      .filter((shipment) => selectedPriceIds.includes(shipment.price._id))
      .map((shipment) => shipment.price.shipmentId);

    if (isNCHL) {
      const shipmentHasNoPrice =
        _watchShipment?.shipmentId && !priceShipmentIds.includes(_watchShipment.shipmentId);
      if (shipmentHasNoPrice) {
        setInvalidShipments([_watchShipment.shipmentId]);
        setOpenDialog(true);
        return;
      }
    } else {
      const shipmentsWithoutPrice = (Array.isArray(_watchShipment) ? _watchShipment : []).filter(
        (s) => !priceShipmentIds.includes(s.shipmentId)
      );
      if (shipmentsWithoutPrice.length > 0) {
        setInvalidShipments(shipmentsWithoutPrice.map((s) => s.shipmentId));
        setOpenDialog(true);
        return;
      }
    }
    const shipmentIds = isNCHL
      ? (data.shipment?._id ? [data.shipment._id] : [])
      : (Array.isArray(data.shipment) ? data.shipment.filter((s) => s._id).map((s) => s._id) : []);
    const payload = {
      ...data,
      entity: data.entity?._id || data.entity,
      account: data.account?._id || data.account,
      remittanceRemarks: data.remittanceRemarks || "",
      bankName: data.bankName || "",
      driver: data.deliveryType === "Home Delivery" ? data.driver?.value : undefined,
      responsibleUser: data.deliveryType === "Branch Delivery" ? data.responsible?.id : undefined,
      collectedAmount: data.collectedAmount,
      shipment: shipmentIds,
      priceIds: data.priceIds?.map((p) => p._id) || [],
    };

    onSubmit(payload);
  }, [_watchPriceIds, _watchShipment, pricingData, selectedPriceIds, onSubmit, isNCHL]);

  const allSelected = pricingData.length > 0 && selectedPriceIds.length === pricingData.length;

  return (
    <AppForm
      loading={
        isBusy ||
        loadingUserShipments ||
        loadingBankNameOptions ||
        loadingAccounts ||
        loadingEntities ||
        loadingDrivers ||
        loadingDriverShipments
      }
    >
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <>
              <Grid item md={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Account"
                      required
                      value={field.value || ""}
                      onChange={(_, data) => {
                        handleAccountChange(data);
                      }}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) => option?._id === value?._id}
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled={isAccountAdmin}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      required
                      label="Entity"
                      value={field.value || ""}
                      onChange={(_, data) => {
                        handleEntityChange(data);
                      }}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) => option?._id === value?._id}
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={6}>
            <Controller
              name="collectionMode"
              control={control}
              rules={{ required: "Please select a collection method." }}
              render={({ field }) => (
                <BaseSelect
                  fullWidth
                  label="Collection Mode"
                  options={_mappedAccountTypeList}
                  value={field.value || ""}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue("remittanceRemarks", "");
                  }}
                  error={!!errors.collectionMode}
                  helperText={errors.collectionMode?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="deliveryType"
              control={control}
              rules={{ required: "Delivery Type is required" }}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors?.deliveryType}>
                  <InputLabel id="delivery-type-label">Delivery Type</InputLabel>
                  <Select
                    labelId="delivery-type-label"
                    label="Delivery Type"
                    value={field.value || ""}
                    onChange={(e) => {
                      handleDeliveryTypeChange(e.target.value);
                    }}
                    required
                  >
                    <MenuItem value="Home Delivery">Driver Delivery</MenuItem>
                    <MenuItem value="Branch Delivery">User Delivery</MenuItem>
                  </Select>
                  {errors.deliveryType && (
                    <FormHelperText error>{errors.deliveryType.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          {_watchDeliveryType === "Home Delivery" && (
            <Grid item md={6}>
              <Controller
                name="driver"
                control={control}
                rules={{ required: "Please select a driver." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Driver"
                    value={field.value || ""}
                    onChange={(_, data) => {
                      handleDriverChange(data);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    options={drivers || []}
                    loading={loadingDrivers}
                    loadingText="Loading Drivers..."
                    helperText={driversError ? "Error loading drivers" : errors?.driver?.message}
                    error={!!errors?.driver || !!driversError}
                  />
                )}
              />
            </Grid>
          )}

          {_watchDeliveryType === "Branch Delivery" && (
            <Grid item md={6}>
              <Controller
                name="responsible"
                control={control}
                rules={{ required: "Please select a delivered user." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Delivered User"
                    loading={loadingUsers || fetchingUsers}
                    loadingText="Loading Delivered Users..."
                    options={_mappedUsers}
                    value={field.value || null}
                    onChange={(_, data) => {
                      handleResponsibleChange(data);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    error={!!errors?.responsible}
                    helperText={errors?.responsible?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={6}>
            <Controller
              name="shipment"
              control={control}
              rules={{
                required: isNCHL
                  ? "Please select a shipment."
                  : "Please select at least one shipment.",
              }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple={!isNCHL}
                  label="Shipment ID"
                  required
                  value={isNCHL ? (value || null) : (Array.isArray(value) ? value : [])}
                  onChange={(_, data) => {
                    handleShipmentChange(data);
                  }}
                  getOptionLabel={(option) => option?.shipmentId || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id && value?._id
                      ? option._id.toString() === value._id.toString()
                      : false
                  }
                  options={getCurrentShipmentOptions()}
                  loading={loadingDrivers || loadingUserShipments || loadingDriverShipments}
                  loadingText="Loading Shipments..."
                  helperText={errors?.shipment?.message}
                  error={!!errors?.shipment}
                  disabled={
                    (_watchDeliveryType === "Home Delivery" && !driverShipments.length) ||
                    (_watchDeliveryType === "Branch Delivery" && !responsibleUserShipments.length)
                  }
                />
              )}
            />
          </Grid>



          {selectedCollectionMethod?.name === "Bank" && (
            <Grid item xs={6}>
              <Controller
                name="bankName"
                control={control}
                rules={{ required: "Please select a bank name." }}
                render={({ field }) => (
                  <BaseSelect
                    fullWidth
                    label="Bank Name"
                    options={formattedBankNameOptions}
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    getOptionLabel={(option) => option.label || ""}
                    getOptionValue={(option) => option.value || ""}
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                    loading={loadingBankNameOptions}
                    loadingText="Loading Bank Names..."
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={6}>
            <Controller
              name="actualAmount"
              control={control}
              rules={{
                required: "Amount is required.",
                min: { value: 0, message: "Amount must be non-negative." },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Actual Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.actualAmount}
                  helperText={errors?.actualAmount?.message}
                  disabled
                />
              )}
            />
          </Grid>

          <Grid item md={6} sx={{ display: "none" }}>
            <Controller
              name="collectedAmount"
              control={control}
              rules={{
                required: "Amount is required.",
                min: { value: 0, message: "Amount must be non-negative." },
                validate: (value) =>
                  Number(value) === Number(_watchActualAmount)
                    ? true
                    : "Collected Amount must match Actual Amount.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Collected Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.collectedAmount}
                  helperText={errors?.collectedAmount?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="remittanceRemarks"
              control={control}
              rules={{ required: "Please enter remittance remarks." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Remittance Remarks"
                  variant="outlined"
                  size="small"
                  required
                  error={!!errors.remittanceRemarks}
                  helperText={errors.remittanceRemarks?.message}
                  inputProps={{ maxLength: 255 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            {isNCHL && !isPaymentDone && unpaidPriceIds.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  variant="outlined"
                  color="success"
                  onClick={async () => {
                    const traceId = await nchlQRRef.current?.handleGenerateQR();
                    if (traceId) setCurrentTraceId(traceId);
                  }}
                  disabled={
                    isBusy ||
                    !_watchPriceIds?.length ||
                    Number(_watchCollectedAmount) <= 0
                  }
                >
                  Pay Via NCHL
                </Button>
                <NCHLQR
                  ref={nchlQRRef}
                  addedUser={loggedInUser?._id}
                  amount={Number(_watchActualAmount) || 0}
                  priceIds={unpaidPriceIds}
                  shipmentId={_watchShipment?.shipmentId || ""}
                  onSuccess={handleNCHLPaymentSuccess}
                />
              </div>
            )}
          </Grid>
          {((isNCHL && _watchShipment?._id) ||
            (!isNCHL && Array.isArray(_watchShipment) && _watchShipment.length > 0)) && (
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={selectedPriceIds.length > 0 && selectedPriceIds.length < pricingData.length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Shipment ID</TableCell>
                        <TableCell>Fee Item</TableCell>
                        <TableCell>Charge</TableCell>
                        <TableCell>Payer</TableCell>
                        <TableCell>Payment Location</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Created At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pricingData.map((shipment) => (
                        <TableRow
                          key={shipment.price._id}
                          sx={{
                            border: invalidShipments.includes(shipment.shipmentId)
                              ? "2px solid red"
                              : "none",
                          }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedPriceIds.includes(shipment.price._id)}
                              onChange={() => handlePriceSelection(shipment.price)}
                            />
                          </TableCell>
                          <TableCell>{shipment.shipmentId}</TableCell>
                          <TableCell>{shipment.price.feeItem}</TableCell>
                          <TableCell>{shipment.price.charge}</TableCell>
                          <TableCell>{shipment.price.payer}</TableCell>
                          <TableCell>{shipment.price.paymentLocation}</TableCell>
                          <TableCell>{shipment.price.user}</TableCell>
                          <TableCell>
                            {new Date(shipment.price.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

          <Grid item md={12}>
            <div style={{ color: "gray", fontSize: 15, marginBottom: ".75rem" }}>
              Notes
            </div>
            <Controller
              name="notes"
              control={control}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={deliveryCollectionDetail?.notes || "<p>Enter a note</p>"}
                  onChange={onChange}
                  height={200}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          disabled={isBusy || !_watchPriceIds?.length || isNCHL}
          onClick={handleSubmit(handleOnSubmit)}
        >
          {deliveryCollectionDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>

      <Dialog open={openDialog} onClose={handleDialogCancel}>
        <DialogTitle>Shipment Fee Item Missing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following shipments have no selected fee items:{" "}
            {invalidShipments.join(", ")}. Want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogConfirm(control._formValues)} autoFocus>
            Yes
          </Button>
          <Button onClick={handleDialogCancel}>No</Button>
        </DialogActions>
      </Dialog>
    </AppForm>
  );
};

export default DeliveryCollectionForm;